import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken') || '');

  // Verify stored token on mount
  useEffect(() => {
    const verifyToken = async () => {
      const stored = localStorage.getItem('authToken');
      if (stored) {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${stored}` }
          });
          setCurrentUser(res.data);
          setAuthToken(stored);
          setIsAuthenticated(true);
        } catch {
          localStorage.removeItem('authToken');
          setAuthToken('');
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      }
    };
    verifyToken();
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await axios.post(`${API_BASE_URL}/api/auth/login`, { username, password });
    const { token, user } = res.data;
    localStorage.setItem('authToken', token);
    setAuthToken(token);
    setCurrentUser(user);
    setIsAuthenticated(true);
    return user;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (authToken) {
        await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      }
    } catch {}
    localStorage.removeItem('authToken');
    setAuthToken('');
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, [authToken]);

  const getAuthHeaders = useCallback(() => {
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  }, [authToken]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated, currentUser, authToken,
      login, logout, getAuthHeaders,
      setCurrentUser, setIsAuthenticated, setAuthToken
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
